import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { openModal } from "./data";
import { EASE, Magnetic } from "./ui-bits";

const LINES = ["THE RIGHT SPACE.", "THE RIGHT TENANT.", "THE RIGHT DEAL."];

const wrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.25 } },
};
const line = {
  hidden: { y: "112%" },
  show: { y: "0%", transition: { duration: 1.1, ease: EASE } },
};

const Hero = ({ ready }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const fade = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex h-[100svh] min-h-[640px] flex-col overflow-hidden"
      data-testid="hero"
    >
      <motion.div className="absolute inset-0" style={{ scale: bgScale }}>
        <motion.img
          src="/assets/img/hero.jpg"
          alt="Bengaluru skyline at dusk"
          className="h-full w-full object-cover"
          initial={{ scale: 1.14 }}
          animate={ready ? { scale: 1 } : {}}
          transition={{ duration: 2.6, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/75 via-obsidian/55 to-obsidian/85" />

      <motion.div
        className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-end px-5 pb-28 md:px-10 md:pb-32"
        style={{ y: textY, opacity: fade }}
      >
        <motion.div
          className="mb-8 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-champagne"
          initial={{ opacity: 0, y: 16 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        >
          <span className="h-px w-10 bg-champagne" />
          Commercial Real Estate Advisory · Bengaluru
        </motion.div>

        <motion.h1
          className="font-serif text-[11.5vw] leading-[0.98] tracking-[-0.01em] text-ivory sm:text-5xl md:text-6xl lg:text-7xl"
          variants={wrap}
          initial="hidden"
          animate={ready ? "show" : "hidden"}
          data-testid="hero-headline"
        >
          {LINES.map((l, i) => (
            <span key={l} className="-mb-[0.08em] block overflow-hidden pb-[0.08em]">
              <motion.span
                className={`block will-change-transform ${i === 2 ? "italic" : ""}`}
                variants={line}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          className="mt-8 max-w-xl text-sm leading-relaxed text-ivory/70 md:text-base"
          initial={{ opacity: 0, y: 24 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
        >
          Elevate Estates connects ambitious businesses with high-potential
          commercial spaces — and property owners with credible, high-quality
          occupiers.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
        >
          <Magnetic>
            <button
              type="button"
              onClick={() => openModal("find")}
              className="group inline-flex items-center gap-3 bg-ivory px-7 py-4 text-[12px] font-medium uppercase tracking-[0.22em] text-obsidian transition-colors duration-500 hover:bg-champagne"
              data-testid="hero-find-space-btn"
            >
              Find a Space
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </Magnetic>
          <Magnetic>
            <button
              type="button"
              onClick={() => openModal("list")}
              className="group inline-flex items-center gap-3 border border-ivory/30 px-7 py-4 text-[12px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:border-champagne hover:text-champagne"
              data-testid="hero-list-property-btn"
            >
              List Your Property
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between px-5 pb-6 text-[10px] font-medium uppercase tracking-[0.3em] text-ivory/50 md:px-10"
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.4 }}
      >
        <span data-testid="hero-location-tag">Bengaluru · India</span>
        <span className="flex items-center gap-2">
          Scroll to explore
          <ArrowDown size={12} className="animate-scroll-hint" />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
